import * as THREE from 'three';
import S from './Registry.js';
import { bus } from './EventBus.js';
import { Time } from './Time.js';
import { Input } from './Input.js';
import { GameState, Phase } from './GameState.js';
import { rand } from './Rand.js';
import { PerfGovernor } from './PerfGovernor.js';
import { createRenderer } from '../render/createRenderer.js';
import { initPortal, getLocalVariantHandle } from './Portal.js';
import { initPortalOverlay } from '../ui/PortalOverlay.js';
import { inspectLocalVariantWorld } from './LocalVariantGate.js';
import {
  safeImport, safeNew, guardUpdate, progress, setStepCount,
  hideBoot, showStart, bootReport, bootMissing, bootTimings,
} from './Boot.js';

// Vite resolves this glob at serve/build time, so subsystems that have not been
// authored yet are simply absent from the map rather than hard import errors.
// `__probe.js` files are the QA harness: they POST measurements to a local
// collector over fetch, which the TRYPORTAL sandbox forbids (`connect-src
// 'none'`) — and they have no business in a shipped bundle anyway.
const MODULES = import.meta.glob([
  '../**/*.js', '!../**/__probe.js',
  // SDK is statically imported by Portal. Installer recovery copies must never
  // become dynamic game modules or make a release depend on backup folder IDs.
  '!../tryportal/**', '!../*.traident-backup-*/**',
]);

// Look up a subsystem module by its path relative to src/core/.
const MOD = (rel) => MODULES[rel];

// Fallback used only if src/render/Engine.js is missing or throws, so the game
// still renders something instead of showing a black screen.
class FallbackEngine {
  constructor(canvas) {
    // Asking for the SAME context the real Engine was just refused is not a
    // fallback. Use the ladder, which starts softer than that request ever was.
    this.renderer = createRenderer(canvas).renderer;
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    this.renderer.setSize(innerWidth, innerHeight);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    this.scene = new THREE.Scene();
    this.scene.fog = new THREE.FogExp2(0x8fa6b8, 0.008);
    this.camera = new THREE.PerspectiveCamera(75, innerWidth / innerHeight, 0.05, 800);

    this.viewScene = new THREE.Scene();
    this.viewCamera = new THREE.PerspectiveCamera(55, innerWidth / innerHeight, 0.005, 12);

    const sun = new THREE.DirectionalLight(0xfff2df, 3.0);
    sun.position.set(48, 70, 26);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.camera.near = 1; sun.shadow.camera.far = 220;
    sun.shadow.camera.left = -70; sun.shadow.camera.right = 70;
    sun.shadow.camera.top = 70; sun.shadow.camera.bottom = -70;
    sun.shadow.bias = -0.0004;
    this.scene.add(sun);
    this.scene.add(new THREE.HemisphereLight(0xbcd8ff, 0x2a2118, 1.1));
    this.viewScene.add(new THREE.HemisphereLight(0xbcd8ff, 0x2a2118, 1.6));
    const vk = new THREE.DirectionalLight(0xffffff, 2.2); vk.position.set(1, 2, 1);
    this.viewScene.add(vk);
    this.isFallback = true;
  }
  setSize(w, h) {
    this.renderer.setSize(w, h);
    this.camera.aspect = w / h; this.camera.updateProjectionMatrix();
    this.viewCamera.aspect = w / h; this.viewCamera.updateProjectionMatrix();
  }
  setQuality() {}
  render() {
    const r = this.renderer;
    r.autoClear = true;
    r.render(this.scene, this.camera);
    r.autoClear = false;
    r.clearDepth();
    r.render(this.viewScene, this.viewCamera);
    r.autoClear = true;
  }
  dispose() { this.renderer.dispose(); }
}

export class Game {
  constructor(canvas) {
    this.canvas = canvas;
    // { fn, always } — `always` entries keep ticking while the game is paused
    // (UI animation, audio, menus). Everything else freezes outside PLAYING.
    this.updaters = [];
    this.running = false;
    this._raf = 0;
    this._worldReadyTimer = 0;
    this._shake = { amp: 0, decay: 0 };
    this._shakeOffset = new THREE.Vector3();
  }

  async boot() {
    setStepCount(12);

    S.state = new GameState();
    S.time = new Time();
    S.input = new Input(this.canvas);
    rand.reseed(S.state.seed);

    // TRYPORTAL, synchronously, before anything slow. The platform posts its
    // BOOTSTRAP exactly once, on the iframe's `load` event — and `load` does
    // NOT wait for dynamic chunks. Loading Portal.js via import() lost that
    // race every time: the chunk was still in flight when the one message this
    // integration exists to receive went past. Static import + a synchronous
    // call here runs during module evaluation, which `load` does wait for.
    // Subscribe before connect can resolve. A fast hosted BOOTSTRAP can answer
    // the first leaderboard read before a dynamic overlay chunk has loaded.
    try { initPortalOverlay(); } catch (e) { console.warn('[boot] portal overlay', e); }
    try { initPortal(); } catch (e) { console.warn('[boot] portal', e); }

    // ---- Prefetch every module, in parallel ------------------------------
    //
    // Boot `await`ed 26 dynamic imports one after another, so each module's
    // round trip was serialised behind the previous one — and the resource
    // timings show them at 365-400 ms each. Nothing about that is necessary:
    // the modules are independent to FETCH, only their construction is ordered.
    //
    // Starting every import here and awaiting the (already in-flight) promise at
    // the point of use keeps the construction sequence exactly as it was, while
    // collapsing 26 sequential round trips into one parallel batch. `import()`
    // caches, so the later `await` is free.
    //
    // Measured breakdown that motivated this — the phases nobody could name
    // before the boot timings existed. Note the shader precompile, which is what
    // I had assumed was the problem, is 75 ms of it:
    //   arena 3731 / vfx 3090 / progression 1484 / renderer 1091 / hud 1068
    //   / spawner 770 / player 617 / audio 420 / precompile 75
    const P = {};
    const pre = (name, rel) => { P[name] = safeImport(name, MOD(rel)); };
    pre('render/Engine', '../render/Engine.js');
    pre('render/Sky', '../render/Sky.js');
    pre('render/Lighting', '../render/Lighting.js');
    pre('render/Materials', '../render/Materials.js');
    pre('math/Formula', '../math/Formula.js');
    pre('world/Collision', '../world/Collision.js');
    pre('world/Kit', '../world/Kit.js');
    pre('world/Arena', '../world/Arena.js');
    pre('vfx/Particles', '../vfx/Particles.js');
    pre('vfx/Decals', '../vfx/Decals.js');
    pre('vfx/Tracers', '../vfx/Tracers.js');
    pre('vfx/Impacts', '../vfx/Impacts.js');
    pre('audio/Audio', '../audio/Audio.js');
    pre('player/Player', '../player/Player.js');
    pre('player/Viewmodel', '../player/Viewmodel.js');
    pre('player/Weapon', '../player/Weapon.js');
    pre('enemy/Spawner', '../enemy/Spawner.js');
    pre('enemy/Ragdoll', '../enemy/Ragdoll.js');
    pre('progression/Progression', '../progression/Progression.js');
    pre('progression/Inventory', '../progression/Inventory.js');
    pre('progression/Fusion', '../progression/Fusion.js');
    pre('progression/Sustain', '../progression/Sustain.js');
    pre('progression/Loot', '../progression/Loot.js');
    pre('ui/Controls', '../ui/Controls.js');
    pre('ui/Forge', '../ui/Forge.js');
    pre('ui/HUD', '../ui/HUD.js');

    // ---- Renderer -------------------------------------------------------
    progress('レンダラを起動中…');
    const engMod = await P['render/Engine'];
    let engine = engMod?.Engine ? safeNew('Engine', () => new engMod.Engine(this.canvas)) : null;
    if (!engine) engine = new FallbackEngine(this.canvas);
    S.engine = engine;
    S.renderer = engine.renderer;
    S.scene = engine.scene;
    S.camera = engine.camera;
    S.viewScene = engine.viewScene || (engine.viewScene = new THREE.Scene());
    S.viewCamera = engine.viewCamera || (engine.viewCamera = new THREE.PerspectiveCamera(55, innerWidth / innerHeight, 0.005, 12));
    S.postfx = engine.postfx || null;

    // ---- Sky / IBL / lighting -------------------------------------------
    progress('大気を生成中…');
    // Engine composes Sky/Lighting/PostFX itself. Adopt whatever it already built
    // and only construct the missing pieces — building a second rig puts two suns
    // and two hemisphere lights in the scene, which blows the sky to pure white.
    S.sky = engine.sky || null;
    S.lighting = engine.lighting || null;

    if (!S.sky) {
      const skyMod = await P['render/Sky'];
      if (skyMod?.Sky) S.sky = safeNew('Sky', () => new skyMod.Sky(S.scene, S.renderer, S.camera));
    }
    if (!S.lighting) {
      const lightMod = await P['render/Lighting'];
      if (lightMod?.Lighting) {
        S.lighting = safeNew('Lighting', () => new lightMod.Lighting(S.scene, S.camera, S.renderer));
      }
    }
    // Engine drives its own sky/lighting from render(); only tick ones we own.
    if (!engine.sky && S.sky?.update) this._tick('Sky', (dt) => S.sky.update(dt));
    if (!engine.lighting && S.lighting?.update) this._tick('Lighting', (dt) => S.lighting.update(dt));

    // ---- Math core ------------------------------------------------------
    progress('公理系を構築中…');
    const formulaMod = await P['math/Formula'];
    if (formulaMod?.Formula) S.formula = safeNew('Formula', () => new formulaMod.Formula(S.state));

    // ---- World ----------------------------------------------------------
    progress('戦域を展開中…');
    const colMod = await P['world/Collision'];

    // Kit resolves Materials.js through an async import and exposes `Kit.ready`,
    // but Arena builds synchronously and never awaited it — so every material
    // request lost the race and the whole level silently fell back to Kit's own
    // textures. Agent A's library (triplanar, Toksvig, macro variation) was
    // never running on a single surface. Await the handshake before building.
    const kitMod = await P['world/Kit'];
    if (kitMod?.Kit?.ready) {
      try { await kitMod.Kit.ready; }
      catch (e) { console.warn('[boot] Kit material handshake failed:', e); }
    }

    const arenaMod = await P['world/Arena'];
    if (arenaMod?.Arena) {
      S.world = safeNew('Arena', () => new arenaMod.Arena(S.scene, colMod?.Collision));
      if (S.world?.update) this._tick('Arena', (dt) => S.world.update(dt));
    }
    if (!S.world) this._buildFallbackGround();
    else await this._adoptWorldMaterials();

    // ---- VFX / Audio ----------------------------------------------------
    progress('効果を初期化中…');
    const vfx = {};
    const partMod = await P['vfx/Particles'];
    if (partMod?.Particles) vfx.particles = safeNew('Particles', () => new partMod.Particles(S.scene));
    const decMod = await P['vfx/Decals'];
    if (decMod?.Decals) vfx.decals = safeNew('Decals', () => new decMod.Decals(S.scene));
    const trMod = await P['vfx/Tracers'];
    if (trMod?.Tracers) vfx.tracers = safeNew('Tracers', () => new trMod.Tracers(S.scene));
    const impMod = await P['vfx/Impacts'];
    if (impMod?.Impacts) vfx.impacts = safeNew('Impacts', () => new impMod.Impacts(vfx));
    S.vfx = vfx;
    for (const [k, v] of Object.entries(vfx)) {
      if (v?.update) this._tick(`vfx.${k}`, (dt) => v.update(dt));
    }

    progress('音響を合成中…');
    const audMod = await P['audio/Audio'];
    if (audMod?.Audio) {
      S.audio = safeNew('Audio', () => new audMod.Audio());
      if (S.audio?.update) this._tick('Audio', (dt) => S.audio.update(dt), true);
      // An AudioContext may only be created from a user gesture, and the game
      // has several ways to reach PLAYING. Rather than depend on one of them
      // being the gesture, take the FIRST gesture there is — whichever it is —
      // and build the context then. `init()` is idempotent, and `unlock()`
      // keeps retrying `resume()` until the context genuinely runs.
      if (S.audio) {
        const arm = () => {
          try { S.audio.init(); S.audio.unlock(); } catch (e) { /* retried next gesture */ }
          if (S.audio.ctx && S.audio.ctx.state === 'running') {
            for (const ev of ['pointerdown', 'keydown', 'touchstart']) {
              window.removeEventListener(ev, arm, true);
            }
          }
        };
        for (const ev of ['pointerdown', 'keydown', 'touchstart']) {
          window.addEventListener(ev, arm, { capture: true, passive: true });
        }
      }
    }

    // ---- Player ---------------------------------------------------------
    progress('オペレータを配備中…');
    const playerMod = await P['player/Player'];
    if (playerMod?.Player) {
      S.player = safeNew('Player', () => new playerMod.Player());
      if (S.player?.update) this._tick('Player', (dt) => S.player.update(dt));
    }
    const vmMod = await P['player/Viewmodel'];
    if (vmMod?.Viewmodel) {
      S.viewmodel = safeNew('Viewmodel', () => new vmMod.Viewmodel(S.viewScene, S.viewCamera));
      if (S.viewmodel?.update) this._tick('Viewmodel', (dt) => S.viewmodel.update(dt));
    }
    const wpMod = await P['player/Weapon'];
    if (wpMod?.Weapon && !S.player?.weapon) {
      S.weapon = safeNew('Weapon', () => new wpMod.Weapon());
      if (S.weapon?.update) this._tick('Weapon', (dt) => S.weapon.update(dt));
    }

    // ---- Enemies --------------------------------------------------------
    progress('敵性体を生成中…');
    const spMod = await P['enemy/Spawner'];
    if (spMod?.Spawner) {
      S.spawner = safeNew('Spawner', () => new spMod.Spawner(S.scene));
      if (S.spawner?.update) this._tick('Spawner', (dt) => S.spawner.update(dt));
    }
    const rgMod = await P['enemy/Ragdoll'];
    if (rgMod?.RagdollSystem) {
      S.ragdolls = safeNew('Ragdolls', () => new rgMod.RagdollSystem(S.scene));
      if (S.ragdolls?.update) this._tick('Ragdolls', (dt) => S.ragdolls.update(dt));
    }

    // ---- Progression ----------------------------------------------------
    progress('進行システムを初期化中…');
    const progMod = await P['progression/Progression'];
    if (progMod?.Progression) {
      S.progression = safeNew('Progression', () => new progMod.Progression(S.state));
      if (S.progression?.update) this._tick('Progression', (dt) => S.progression.update(dt), true);
    }
    const invMod = await P['progression/Inventory'];
    if (invMod?.Inventory) S.inventory = safeNew('Inventory', () => new invMod.Inventory());
    S.fusion = await P['progression/Fusion'];

    const susMod = await P['progression/Sustain'];
    if (susMod?.Sustain) {
      S.sustain = safeNew('Sustain', () => new susMod.Sustain(S.scene));
      if (S.sustain?.update) this._tick('Sustain', (dt) => S.sustain.update(dt));
    }

    const lootMod = await P['progression/Loot'];
    if (lootMod?.Loot) {
      S.loot = safeNew('Loot', () => new lootMod.Loot(S.scene));
      if (S.loot?.update) this._tick('Loot', (dt) => S.loot.update(dt));
    }

    // ---- UI -------------------------------------------------------------
    progress('HUDを描画中…');
    const ctrlMod = await P['ui/Controls'];
    if (ctrlMod?.Controls) {
      S.controls = safeNew('Controls', () => new ctrlMod.Controls(document.getElementById('ui')));
    }
    const forgeMod = await P['ui/Forge'];
    if (forgeMod?.Forge) {
      S.forge = safeNew('Forge', () => new forgeMod.Forge(document.getElementById('ui')));
      if (S.forge?.update) this._tick('Forge', (dt) => S.forge.update(dt), true);
    }
    const hudMod = await P['ui/HUD'];
    if (hudMod?.HUD) {
      S.hud = safeNew('HUD', () => new hudMod.HUD(document.getElementById('ui')));
      if (S.hud?.update) this._tick('HUD', (dt) => S.hud.update(dt), true);
    }

    // Compile every shader NOW, behind the loading screen. Lazily-compiled
    // programs are merely a hitch on Metal; under ANGLE each one is retranslated
    // to HLSL and then to D3D bytecode, which stalls the first seconds of play.
    progress('シェーダを事前コンパイル中…');
    try {
      S.renderer.compile(S.scene, S.camera);
      if (S.viewScene && S.viewCamera) S.renderer.compile(S.viewScene, S.viewCamera);
      console.info(`[boot] precompiled ${S.renderer.info.programs?.length ?? 0} shader programs`);
    } catch (e) { console.warn('[boot] shader precompile failed (non-fatal):', e); }

    // Adapt to whatever machine this actually is, rather than assuming 'high'.
    S.perf = new PerfGovernor();
    this._tick('PerfGovernor', () => S.perf.update(), true);

    progress('最終調整…');
    this._wireGlobalEvents();
    this._wireIntegration();
    window.addEventListener('resize', () => this._onResize());
    this._onResize();

    progress('完了');
    if (bootReport.failed.length) {
      console.warn('[boot] degraded — missing subsystems:', bootReport.failed.map(f => f.name));
    }
    console.info('[boot] loaded:', bootReport.loaded.join(', ') || '(none)');
    if (bootMissing.length) console.info('[boot] not yet authored:', bootMissing.join(', '));

    // Expose for the visual-QA agents to drive.
    // `step()` exists because requestAnimationFrame is throttled to zero in a
    // backgrounded tab — automated screenshot passes must advance frames manually.
    window.AXIOM = {
      S, bus, game: this, THREE, report: bootReport, missing: bootMissing,
      ...(getLocalVariantHandle() ? { localVariants: getLocalVariantHandle() } : {}),
      step: (n = 1, fixedDt = 1 / 60) => {
        for (let i = 0; i < n; i++) {
          S.time.now = performance.now() / 1000 - fixedDt;
          this._frame();
        }
        return S.time.frame;
      },
      // Warm up shader compilation and settle springs before a screenshot.
      settle: (seconds = 1.5) => window.AXIOM.step(Math.round(seconds * 60)),
    };

    /**
     * Plain-data QA surface.
     *
     * A Windows falsification audit returned "could not reproduce" for ten of
     * ten required scenarios, because its browser evaluates in an isolated world
     * and could not see `window.AXIOM` — so no frame timings, no renderer
     * counters, no GPU identity, and every product gate came back null. A build
     * that cannot be measured cannot be certified, and that is a defect in the
     * build, not in the auditor.
     *
     * This returns only JSON-serialisable values and reads nothing it cannot
     * reach synchronously, so it survives structured cloning across an isolated
     * world. It never fabricates: anything unavailable is `null`, never 0.
     */
    window.AXIOM_QA = () => {
      const r = S.renderer, gl = r && r.getContext();
      const gov = S.perf;
      const pct = (arr, p) => {
        if (!arr || !arr.length) return null;
        const a = arr.slice().sort((x, y) => x - y);
        return +a[Math.min(a.length - 1, Math.floor(a.length * p))].toFixed(2);
      };
      const raw = gov ? gov.raw : null;
      // 1% low is the mean of the slowest 1% of frames, expressed as fps.
      let onePctLow = null;
      if (raw && raw.length >= 100) {
        const a = raw.slice().sort((x, y) => y - x);
        const n = Math.max(1, Math.round(a.length * 0.01));
        const mean = a.slice(0, n).reduce((s, v) => s + v, 0) / n;
        onePctLow = +(1000 / mean).toFixed(1);
      }
      let glRenderer = null, glVendor = null;
      try {
        const dbg = gl && gl.getExtension('WEBGL_debug_renderer_info');
        if (dbg) {
          glRenderer = gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL);
          glVendor = gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL);
        }
      } catch (_) { /* blocked by policy on some builds */ }
      const dmg = S.hud && S.hud.damage && S.hud.damage.canvas;
      return {
        bundle: (document.currentScript && document.currentScript.src) || null,
        phase: S.state && S.state.phase,
        frames: raw ? raw.length : null,
        frameMs: raw && raw.length ? {
          p50: pct(raw, 0.5), p95: pct(raw, 0.95), p99: pct(raw, 0.99),
          max: +Math.max(...raw).toFixed(2),
        } : null,
        onePctLowFps: onePctLow,
        worstFrameMs: gov ? +gov.worstMs.toFixed(1) : null,
        hitches: gov ? gov.hitches.slice(-50) : null,
        quality: S.engine && S.engine.quality,
        renderScale: S.engine && S.engine.renderScale,
        autoApply: gov ? gov.autoApply : null,
        governorChanges: gov ? gov.changes.slice(-20) : null,
        drawingBuffer: gl ? [gl.drawingBufferWidth, gl.drawingBufferHeight] : null,
        cssViewport: [window.innerWidth, window.innerHeight],
        devicePixelRatio: window.devicePixelRatio,
        pixelRatio: r ? r.getPixelRatio() : null,
        damageCanvas: dmg ? [dmg.width, dmg.height] : null,
        shadowMaps: S.lighting && S.lighting.csm
          ? S.lighting.csm.lights.map((l) => l.shadow.mapSize.x) : null,
        bloom: S.postfx ? (S.postfx.bloomPass.enabled ? S.postfx.bloomRes : 0) : null,
        draws: r ? r.info.render.calls : null,
        triangles: r ? r.info.render.triangles : null,
        programs: r ? r.info.programs.length : null,
        textures: r ? r.info.memory.textures : null,
        geometries: r ? r.info.memory.geometries : null,
        glRenderer, glVendor,
        contextLost: gl ? gl.isContextLost() : null,
        audioNodes: S.audio && S.audio._voices != null ? S.audio._voices : null,
        heapMB: performance.memory
          ? +(performance.memory.usedJSHeapSize / 1048576).toFixed(1) : null,
        enemies: S.spawner && S.spawner.active ? S.spawner.active.length : null,
        wave: S.spawner ? S.spawner.wave : null,
        arenaStageMs: (S.world && S.world.stageMs) || null,
        arenaMergeStats: (S.world && S.world.mergeStats) || null,
        matTimings: (typeof window !== 'undefined' && window.__AX_MAT_TIMINGS) || null,
        arenaBuildMs: (S.world && S.world.buildMs) ? +S.world.buildMs.toFixed(1) : null,
        bootTimings: bootTimings.slice(),
        bootTotalMs: bootTimings.length
          ? +bootTimings[bootTimings.length - 1].at.toFixed(1) : null,
        pointerLocked: !!document.pointerLockElement,
        fallbackLook: !!(S.input && S.input.fallbackLook),
      };
    };
    // Compact, deterministic state for browser-game automation. Coordinates are
    // world-space metres; +x is right, +y is up, and +z follows the arena axis.
    window.render_game_to_text = () => {
      const p = S.player;
      const weapon = p?.weapon;
      const screen = S.hud?.screens?.current ?? null;
      const point = (v) => v ? {
        x: +Number(v.x || 0).toFixed(2),
        y: +Number(v.y || 0).toFixed(2),
        z: +Number(v.z || 0).toFixed(2),
      } : null;
      return JSON.stringify({
        phase: S.state?.phase ?? null,
        screen,
        player: p ? {
          position: point(p.position),
          yaw: +Number(p.yaw || 0).toFixed(3),
          pitch: +Number(p.pitch || 0).toFixed(3),
          hp: Number.isFinite(p.hp) ? p.hp : null,
          alive: p.alive !== false,
        } : null,
        input: {
          move: S.input?.moveAxis?.() ?? { x: 0, z: 0 },
          gamepad: S.input?.gamepadInfo?.() ?? { connected: false },
          bindings: S.input?.bindings ?? null,
        },
        weapon: weapon ? {
          id: weapon.id ?? weapon.name ?? null,
          mag: weapon.ammo ?? weapon.mag ?? null,
          reserve: weapon.reserve ?? null,
          reloading: !!(weapon.isReloading || weapon.reloading),
          ads: !!(weapon.adsActive || weapon.ads),
        } : null,
        wave: S.spawner?.wave ?? null,
        enemies: S.spawner?.active?.filter?.((enemy) => enemy?.alive)?.length ?? 0,
        forgeOpen: !!S.forge?.open,
        perkDraftOpen: !!S.hud?.perkDraft?.open,
      });
    };
    return bootReport;
  }

  // Any level material that still came from Kit's own set (names Materials.js
  // isn't contracted to provide, plus aliases) gets the shading library grafted
  // on: macro-scale albedo/roughness breakup to kill countable tiling, the
  // Toksvig roughness floor that stops specular fireflies, and detail normals.
  // `Mat.adopt` is idempotent — already-patched materials return untouched.
  async _adoptWorldMaterials() {
    // NOT the boot prefetch map — that is a local inside `boot()`. This method is
    // called again on restart, long after boot's scope is gone.
    const matMod = await safeImport('render/Materials', MOD('../render/Materials.js'));
    const Mat = matMod?.Mat;
    if (!Mat?.adopt || !S.world?.group) return;

    // Metres per texture tile, chosen so features land at believable real sizes.
    const SCALE = {
      brick: 3.6, concrete: 2.5, concreteDark: 2.5, asphalt: 1.45, sand: 3.0,
      rustedMetal: 1.9, paintedMetal: 1.7, gunmetal: 1.2, metal: 1.9,
    };

    const seen = new Set();
    let adopted = 0;
    S.world.group.traverse((o) => {
      const mats = Array.isArray(o.material) ? o.material : (o.material ? [o.material] : []);
      for (const m of mats) {
        if (!m || seen.has(m.uuid)) continue;
        seen.add(m.uuid);
        // Only lit, opaque surfaces. Unlit helpers (contact shadows, decals) and
        // transparent glass must keep their own shading.
        if (!m.isMeshStandardMaterial || m.transparent) continue;
        if (m.userData?.axUniforms) continue;      // already a library material
        const key = (m.name || '').replace(/^kit:/, '');
        try { Mat.adopt(m, { worldScale: SCALE[key] ?? 2.0 }); adopted++; }
        catch (e) { console.warn('[boot] adopt failed for', m.name, e); }
      }
    });
    if (adopted) console.info(`[boot] adopted ${adopted} level materials into the shading library`);
  }

  _buildFallbackGround() {
    const g = new THREE.Mesh(
      new THREE.PlaneGeometry(400, 400, 1, 1),
      new THREE.MeshStandardMaterial({ color: 0x4a4f55, roughness: 0.95, metalness: 0 }),
    );
    g.rotation.x = -Math.PI / 2;
    g.receiveShadow = true;
    S.scene.add(g);
    S.world = {
      collide: (pos) => { if (pos.y < 1.7) pos.y = 1.7; return pos; },
      raycast: () => ({ hit: false }),
      groundHeight: () => 0,
      isFallback: true,
    };
  }

  // Put a token into the bag, or into the world if the bag will not take it.
  // The one rule: it is never destroyed on the way.
  stow(token) {
    if (!token) return false;
    if (S.inventory?.add(token)) {
      S.hud?.toast?.('手持ちへ — TAB で装着');
      return true;
    }
    return false;   // `inventory:full` already fired; the handler re-drops it
  }

  // Register a per-frame updater. `always` keeps it running while paused.
  _tick(name, fn, always = false) {
    this.updaters.push({ fn: guardUpdate(name, fn), always });
  }

  // Cross-territory wiring. Each of these spans two agents' modules, so no single
  // agent could own it: one side emits or declares, the other never subscribes.
  _wireIntegration() {
    const st = S.state;

    // 1. Nothing accumulated run damage. GameState declares totalDamage/peakDamage,
    //    Screens and HUD read them, Ascension banks from them — but no producer
    //    ever wrote them, so every run banked 0 Axiom Points.
    bus.on('hit:enemy', (e) => {
      const d = e && e.damage;
      if (!d || typeof d.gt !== 'function') return;
      st.totalDamage = st.totalDamage ? st.totalDamage.add(d) : d;
      if (!st.peakDamage || d.gt(st.peakDamage)) st.peakDamage = d;
      if (e.isPrime) st.primeCrits++;
    });

    // 2. THE CORE LOOP WAS INERT. Nine modules subscribe to `enemy:death` (kill
    //    feed, VFX, audio, loot, spawner, progression) and not one of them awarded
    //    XP — `GameState.addXP` was defined and never called by anybody. So no
    //    kill ever produced a level, and no level ever produced a perk draft.
    //    Kill -> XP -> level -> draft is the entire roguelite spine.
    bus.on('enemy:death', (p) => {
      const xp = (p && Number.isFinite(p.xp)) ? p.xp : 1;
      st.kills++;
      st.addXP(xp);
    });

    // Wave counter lives on the Spawner; the HUD and death screen read GameState.
    bus.on('wave:start', ({ wave }) => { st.wave = wave; });

    // Level-up hands off to the perk draft. Progression owns choosing the three
    // cards; we own the phase change and restoring play afterwards.
    bus.on('perk:offer', () => st.setPhase(Phase.DRAFT));

    // The draft UI emits perk:choose/reroll/banish and had no consumer. It also
    // calls `S.progression.choose(perk, index)` directly — but Progression.choose
    // takes an INDEX or an ID STRING, so a perk object matched neither branch and
    // it returned false silently: the card lit up as chosen and nothing happened.
    // choose() is guarded by `this.open`, so the UI's own direct call is a no-op
    // and this cannot double-apply.
    bus.on('perk:choose', ({ perk, index }) => S.progression?.choose?.(perk?.id ?? index));
    bus.on('perk:reroll', () => S.progression?.reroll?.());
    bus.on('perk:banish', ({ perk, index }) => S.progression?.banish?.(perk?.id ?? index));

    // Progression already decrements pendingPerks itself — only own the phase.
    bus.on('perk:acquired', () => {
      if (st.pendingPerks <= 0 && st.phase === Phase.DRAFT) st.setPhase(Phase.PLAYING);
    });

    // 3. Tokens used to be destroyed on pickup once the formula filled up: Loot
    //    emitted `loot:prompt`, no UI ever answered it, and after 3s it resolved
    //    to -1 = discard. Slots are also fixed at 4 and `addSlot()` was never
    //    called anywhere, so from the 5th pickup on, every drop in the run was
    //    thrown away. Tokens now go to the inventory and the Forge equips them.
    bus.on('loot:prompt', ({ token }) => {
      S.loot?.resolvePrompt?.(-1);        // release Loot's 3s timer immediately
      if (token) this.stow(token);
    });

    // The magnet refuses drops it has nowhere to put. Say so, or the guard is
    // indistinguishable from a bug: the player just sees loot they cannot take.
    bus.on('loot:blocked', () => {
      S.hud?.toast?.('手持ちが満杯 — TAB で整理すると拾えます');
      bus.emit('sfx', { name: 'ui_deny', volume: 0.4 });
    });

    // A full bag must never eat a pickup. Try to make room by fusing, and if the
    // bag still cannot take it, put the token BACK IN THE WORLD rather than
    // destroying it — the player can clear space in the Forge and come collect it.
    bus.on('inventory:full', ({ token }) => {
      // No automatic fusion here. Fusing without asking rewrote the player's bag
      // — it collapsed three distinct moduli into one, fused spare curses into a
      // worse curse, and burned tier-V tetration tokens for zero gain because
      // `apply()` saturates at ↑↑6. Fusion is a Forge decision now.
      const pos = S.player?.position;
      if (!pos || !S.loot?.spawn) return;
      const p = pos.clone();
      p.x += (rand.next() - 0.5) * 2.4;
      p.z += (rand.next() - 0.5) * 2.4;
      // spawn() returns null when the ground is already saturated. In that case
      // the token stays refused rather than being handed to a destructor.
      if (S.loot.spawn(token, p)) S.hud?.toast?.('手持ちが満杯 — 足元に戻しました');
      else S.hud?.toast?.('手持ちも地面も満杯 — TAB で整理してください');
    });

    // The ground pool is finite. Saying so beats silently overwriting a drop.
    bus.on('loot:poolFull', () => {
      S.hud?.toast?.('地面のドロップが上限です — TAB で手持ちを整理してください');
    });

    // The expression grows with the operator hierarchy: a build capped at four
    // slots forever cannot climb from addition to tetration.
    bus.on('player:levelup', ({ level }) => {
      if (level % 3 === 0) S.formula?.addSlot?.();
      // The bag also has to grow. Eleven operators cannot fuse at all, so a
      // fixed 24 slots saturates permanently around wave 16 and roughly 73% of
      // every later drop becomes uncollectable — the loot economy simply dies.
      if (S.inventory) S.inventory.capacity = Math.min(64, 24 + level * 2);
    });

    // 4. Viewmodel emits `vfx:shell` (not in the original event table) and nothing
    //    listened, so the weapon ejected no casings.
    const shells = S.vfx?.particles;
    if (shells?.ejectShell) {
      bus.on('vfx:shell', (p) => {
        if (!p || !p.position) return;
        shells.ejectShell(p.position, p.direction || p.dir || { x: 1, y: 0.6, z: 0 }, p);
      });
    }

    // 4. Menu buttons emitted UI intents with no consumer.
    // Start means a fresh deployment. Once the frame loop exists, returning
    // from start() would leave a quit/dead player paused in MENU forever.
    bus.on('ui:start', ({ source } = {}) => { if (this.running) this.restart({ requestLock: source !== 'gamepad' }); else this.start({ requestLock: source !== 'gamepad' }); });
    bus.on('ui:resume', ({ source } = {}) => { st.setPhase(Phase.PLAYING); if (source !== 'gamepad') S.input.requestLock(); });
    bus.on('ui:pause', () => { st.setPhase(Phase.MENU); S.input.exitLock(); });
    bus.on('ui:restart', ({ source } = {}) => this.restart({ requestLock: source !== 'gamepad' }));
    bus.on('ui:quit', () => { st.setPhase(Phase.MENU); S.input.exitLock(); });

    // 5. The viewmodel is authored in CAMERA-LOCAL space (right/down/forward from
    //    the origin), but it was added to viewScene's root while Engine copies the
    //    player's world transform onto viewCamera every frame. The gun was
    //    therefore parked at the world origin, ~47m behind the player, and culled.
    //    Parenting it to the camera makes the local authoring correct and keeps its
    //    world position accurate for lighting.
    //    Note: `viewmodel.root` is NOT the mesh container — the geometry lives in a
    //    separate group added straight to viewScene, so re-parenting `.root` moves
    //    an empty node. Find the child that actually carries meshes.
    if (S.viewScene && S.viewCamera) {
      const holder = S.viewScene.children.find((c) => {
        if (c.isLight || c.isCamera) return false;
        let n = 0;
        c.traverse((o) => { if (o.isMesh || o.isSprite) n++; });
        return n > 0;
      });
      if (holder && holder.parent !== S.viewCamera) {
        S.viewCamera.add(holder);
        holder.position.set(0, 0, 0);
        holder.quaternion.identity();
      }
    }

    // 6. The wave director has a start() that nothing ever called, so `running`
    //    stayed false and no enemy ever spawned.
    bus.on('state:phase', ({ phase, prev }) => {
      if (phase === Phase.PLAYING && prev !== Phase.DRAFT && prev !== Phase.FORGE) {
        if (S.spawner && !S.spawner.running) S.spawner.start?.();
        // Keep ordinary/public runs completely unchanged. Only the explicit,
        // loopback-local acceptance handle needs the diagnostic next-turn
        // readiness signal.
        if (getLocalVariantHandle()) this._queueWorldReady();
      }
    });

    // 7. The Arena authors a spawn point and the Player has its own standalone
    //    default — both correct in isolation, but nothing connected them, so the
    //    player spawned at the origin, ~11m under the courtyard floor.
    this._placePlayerAtSpawn();
    bus.on('player:respawn', () => this._placePlayerAtSpawn());

    // 8. Depth of field had a HARD-CODED 12m focus plane that nothing ever
    //    updated, so aiming at anything not exactly 12m away blurred the target
    //    — the opposite of what ADS is for. Autofocus on whatever is down the
    //    barrel, the way a real optic (and every shooter) behaves.
    this._focus = 12;
    this._focusRay = { o: new THREE.Vector3(), d: new THREE.Vector3() };
    this._tick('DOFAutofocus', () => {
      const pf = S.postfx;
      if (!pf?.set || !S.camera) return;
      const w = S.player?.weapon;
      if (!w || !w.adsActive) return;             // only matters while aiming
      // ...and only when there is a DOF pass to focus. At `low` the pass is off,
      // so the raycast below (plus a walk of every live enemy) would compute a
      // focus distance for a blur that is never applied — on precisely the
      // machine that chose `low` because it had nothing to spare.
      if (pf.dofPass && pf.dofPass.enabled === false) return;
      const { o, d } = this._focusRay;
      o.copy(S.camera.position);
      d.set(0, 0, -1).applyQuaternion(S.camera.quaternion);
      let dist = 60;
      const hit = S.world?.raycast?.(o, d, 200);
      if (hit?.hit && hit.point) dist = o.distanceTo(hit.point);
      // Enemies sit in front of walls — prefer the nearest one near the crosshair.
      for (const e of (S.spawner?.active || [])) {
        if (!e?.alive || !e.position) continue;
        const toE = e.position.clone().sub(o);
        const along = toE.dot(d);
        if (along <= 0.5 || along > dist) continue;
        if (toE.addScaledVector(d, -along).length() < (e.radius ?? 0.6) + 0.35) dist = along;
      }
      // Smooth so the blur never pumps while the reticle drifts.
      this._focus += (dist - this._focus) * 0.25;
      pf.set('dofFocus', this._focus);
      // A fixed 2.2m in-focus band is far too tight to hold a whole target, let
      // alone the enemy standing next to it. Scale the band with range.
      const u = pf.dofPass?.material?.uniforms?.focusRange;
      if (u) u.value = Math.min(14, Math.max(3.5, this._focus * 0.3));
    });

    // 9. Give the VFX layer what it needs from systems built after it.
    if (S.camera) S.vfx?.particles?.setCamera?.(S.camera);
    if (S.sky?.sunDirection) {
      S.vfx?.particles?.setSunDirection?.(S.sky.sunDirection, S.sky.sunColor);
    }
  }

  // Move the player onto the arena's authored spawn pad, facing the enemy side.
  // Falls back to a ground-height query, then to leaving the player where it is.
  _placePlayerAtSpawn() {
    const p = S.player, w = S.world;
    if (!p || !w) return;
    const sp = w.playerSpawn;
    if (sp) {
      p.position.set(sp.x, sp.y, sp.z);
      if (typeof w.playerSpawnYaw === 'number') p.yaw = w.playerSpawnYaw;
    } else if (typeof w.groundHeight === 'function') {
      p.position.y = w.groundHeight(p.position.x, p.position.z) + 0.1;
    }
    p.velocity?.set?.(0, 0, 0);
  }

  // A local-acceptance lane is never allowed to infer that a visible start
  // equals a usable world. The first Controls sheet can synchronously return
  // the state to MENU, and hosted boot can outlast the SDK handshake. Check on
  // the next turn so camera, spawner and the equipped weapon are actual live
  // objects; failed checks are diagnostic-only and never stage a score run.
  _queueWorldReady() {
    if (this._worldReadyTimer) clearTimeout(this._worldReadyTimer);
    this._worldReadyTimer = setTimeout(() => {
      this._worldReadyTimer = 0;
      const world = inspectLocalVariantWorld({
        state: S.state, spawner: S.spawner, camera: S.camera,
        player: S.player, controls: S.controls,
      });
      bus.emit(world.ready ? 'game:worldReady' : 'game:worldNotReady', world);
    }, 0);
  }

  restart({ requestLock = true } = {}) {
    S.state.reset();
    // Spawner.reset() clears the wave but leaves `running` true, so the
    // `if (!running) start()` guard in start() never fires and the director sits
    // in IDLE forever — restarting produced a world with no enemies at all.
    S.spawner?.reset?.();
    S.spawner?.start?.();
    // The bag and the ground are run-scoped. Leaving them meant restarting at
    // wave 1 with 24 carried tokens and 64 tier-IV/V drops already on the floor.
    S.inventory?.clear?.();
    S.loot?.clear?.();
    S.player?.respawn?.();
    S.formula?.reset?.();
    S.vfx?.particles?.clear?.();
    S.state.setPhase(Phase.PLAYING);
    if (requestLock) S.input.requestLock();
  }

  _wireGlobalEvents() {
    bus.on('camera:shake', ({ amount = 0.1, duration = 0.25 }) => {
      this._shake.amp = Math.max(this._shake.amp, amount);
      this._shake.decay = 1 / Math.max(0.02, duration);
    });

    bus.on('player:died', () => {
      S.state.setPhase(Phase.DEAD);
      S.input.exitLock();
      // Death is a full stop, applied by the phase handler below.
    });

    // A menu that does not stop the world is not a menu. Every non-PLAYING phase
    // is a hard pause: timescale 0 and gameplay updaters skipped entirely.
    bus.on('state:phase', ({ phase }) => {
      if (phase === Phase.PLAYING) {
        S.time.setScale(1, 1);        // snap — resuming must not ramp either
      } else {
        S.time.setScale(0, 1);        // snap — no easing into a pause
        S.input.exitLock();           // give the cursor back for menu interaction
      }
    });

    // Esc pauses; clicking the canvas resumes.
    bus.on('input:pointerlock', (locked) => {
      const st = S.state;
      // Only a genuine EXIT pauses the game. Treating every unlocked state as a
      // pause meant that on any browser refusing pointer lock, the click that
      // starts a run put the player in PLAYING and the immediately-following
      // `pointerlock(false)` threw them back to the menu — an unbreakable loop
      // with no explanation. Pausing is what happens when a player who HAD the
      // lock presses Esc, so require that they had it.
      if (locked) { this._hadLock = true; return; }
      if (this._hadLock && st.phase === Phase.PLAYING) {
        this._hadLock = false;
        st.setPhase(Phase.MENU);
      }
    });
    bus.on('input:fallbackLook', (on) => {
      if (on) S.hud?.toast?.('マウスロックを取得できません — ドラッグで視点操作します');
    });
    this.canvas.addEventListener('click', () => {
      const st = S.state;
      if (st.phase === Phase.MENU || st.phase === Phase.PLAYING) {
        S.input.requestLock();
        st.setPhase(Phase.PLAYING);
      }
    });
  }

  _onResize() {
    const w = innerWidth, h = innerHeight;
    S.engine?.setSize?.(w, h);
    if (S.camera) { S.camera.aspect = w / h; S.camera.updateProjectionMatrix(); }
    if (S.viewCamera) { S.viewCamera.aspect = w / h; S.viewCamera.updateProjectionMatrix(); }
    S.hud?.resize?.(w, h);
  }

  start({ requestLock = true } = {}) {
    // Audio first, and OUTSIDE the running guard. `start()` returns early when
    // the loop is already up, which meant any path that reached PLAYING by
    // another route left the AudioContext uncreated — silent game, no error.
    S.audio?.init?.();
    S.audio?.unlock?.();
    if (this.running) return;
    this.running = true;
    S.state.setPhase(Phase.PLAYING);
    if (S.spawner && !S.spawner.running) S.spawner.start?.();
    if (requestLock) S.input.requestLock();
    hideBoot();
    const loop = () => {
      this._raf = requestAnimationFrame(loop);
      this._frame();
    };
    this._raf = requestAnimationFrame(loop);
  }

  _frame() {
    const t = S.time;
    // Cost of the work we control, with the vsync wait excluded. PerfGovernor
    // judges on this rather than on the frame interval, which under rAF is just
    // the display period and says nothing about how loaded the machine is.
    const frameT0 = performance.now();
    t.tick();
    const dt = t.dt;
    S.input?.beginFrame?.(t.rawDt || dt || 1 / 60);

    // Outside PLAYING the world is genuinely frozen: enemies stop, the spawner
    // stops, projectiles stop. Only UI/audio keep ticking so menus stay alive.
    const paused = S.state.phase !== Phase.PLAYING;
    S.state.advanceRunTime(dt);
    for (let i = 0; i < this.updaters.length; i++) {
      const u = this.updaters[i];
      if (u.always) u.fn(t.rawDt);   // UI must animate at full speed while paused
      else if (!paused) u.fn(dt);
    }

    // Camera shake, applied after all systems have set the base transform.
    if (this._shake.amp > 0.0001 && S.camera) {
      const a = this._shake.amp;
      this._shakeOffset.set(
        (rand.next() * 2 - 1) * a,
        (rand.next() * 2 - 1) * a,
        (rand.next() * 2 - 1) * a * 0.5,
      );
      S.camera.position.add(this._shakeOffset);
      S.camera.rotateZ((rand.next() * 2 - 1) * a * 0.25);
      this._shake.amp -= this._shake.amp * this._shake.decay * t.rawDt * 4;
      if (this._shake.amp < 0.0005) this._shake.amp = 0;
    }

    if (S.audio?.setListener && S.camera) {
      S.audio.setListener(S.camera.position, S.camera.quaternion);
    }

    // While paused the world is frozen, so re-rendering it at full quality every
    // frame buys the player nothing and costs them everything: menus, the pause
    // screen, the controls overlay and the Forge were all running the full CSM +
    // GTAO + 10-pass post chain at 60 Hz over a static scene. The UI itself must
    // stay smooth, so throttle only the 3D to ~20 Hz and let the DOM run free.
    let drawWorld = true;
    if (paused) {
      this._pausedFrames = (this._pausedFrames || 0) + 1;
      drawWorld = (this._pausedFrames % 3) === 1;   // ~20 Hz of a motionless scene
    } else {
      this._pausedFrames = 0;
    }
    if (drawWorld) {
      try { S.engine.render(dt); }
      catch (e) { console.error('[render]', e); }
    }

    S.perf?.sample?.(performance.now() - frameT0);
    S.input.endFrame();
  }

  stop() {
    this.running = false;
    cancelAnimationFrame(this._raf);
  }
}
